import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowUpRight,
  Star,
  Sparkles,
  Instagram,
  MapPin,
  Mail,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { SITE } from "@/lib/site";
import { API } from "@/lib/auth";
import {
  IMG,
  HAIR_SERVICES,
  EXTENSION_METHODS,
  HAIR_LOSS,
  REVIEWS,
  FAQ as FALLBACK_FAQ,
  INSTA_TILES,
} from "@/lib/data";

const DEFAULT_HERO = {
  eyebrow: "Now booking",
  title_line_1: "Hair.",
  title_line_2: "Education.",
  title_line_3: "Empowerment.",
  subhead: SITE.subtitle,
  image: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-hair-academy-2/artifacts/wmvhzhru_IMG_0794.jpeg",
  stat_years: "8+",
  stat_reviews: "5.0",
  review_quote: "Most welcoming salon with a fantastic atmosphere — wouldn't go anywhere else.",
  review_author: "Erin A.",
};

const Hero = ({ hero }) => (
  <section
    data-testid="home-hero"
    className="relative pt-28 sm:pt-32 pb-14 sm:pb-20"
  >
    <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
      <div className="grid gap-10 lg:grid-cols-12 lg:gap-14 items-end">
        <div className="lg:col-span-7">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-[52px] leading-[0.95] sm:text-7xl lg:text-[92px] mt-5 tracking-tight text-[hsl(var(--onyx))] text-balance"
          >
            {hero.title_line_1}<br />
            <span className="italic font-light">{hero.title_line_2}</span><br />
            <span className="text-[hsl(var(--gold))] font-editorial">{hero.title_line_3}</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base sm:text-lg text-[hsl(var(--onyx)/0.65)] leading-relaxed"
          >
            {hero.subhead}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-book-appointment"
              className="btn-pill btn-primary"
            >
              <span aria-hidden="true">🖤</span> Book Your Appointment <ArrowUpRight size={16} />
            </a>
            <Link
              to="/shop"
              data-testid="hero-shop-extensions"
              className="btn-pill btn-blush"
            >
              <span aria-hidden="true">🩷</span> Shop NøoksProRange
            </Link>
            <Link
              to="/academy"
              data-testid="hero-view-courses"
              className="btn-pill btn-outline"
            >
              <span aria-hidden="true">🤍</span> Explore Training
            </Link>
          </motion.div>

          <div className="mt-14 grid grid-cols-3 gap-6 max-w-xl">
            {[
              { k: hero.stat_years, v: "Years of experience" },
              { k: `${hero.stat_reviews}★`, v: "Google reviews" },
              { k: "Academy", v: "Certified courses" },
            ].map((s) => (
              <Reveal key={s.v} delay={0.1} className="text-left">
                <div className="font-display text-3xl sm:text-4xl text-[hsl(var(--onyx))]">
                  {s.k}
                </div>
                <div className="mt-1 text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">
                  {s.v}
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative aspect-[4/5] rounded-[2rem] overflow-hidden hover-zoom"
            >
              <img
                src={hero.image}
                alt="Signature Malek Dridi finish"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -bottom-8 -left-6 sm:-left-10 glass rounded-3xl p-5 w-[240px] sm:w-[280px]"
            >
              <div className="flex items-center gap-1 text-[hsl(var(--gold))]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <p className="font-display text-lg leading-snug mt-2 text-[hsl(var(--onyx))]">
                “{hero.review_quote}”
              </p>
              <p className="mt-2 text-xs text-[hsl(var(--onyx)/0.55)]">
                {hero.review_author} · Google Review
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.8 }}
              className="absolute -top-4 -right-4 sm:-right-6 glass-dark rounded-full px-5 py-3 flex items-center gap-2 text-[hsl(var(--cream))]"
            >
              <Sparkles size={14} className="text-[hsl(var(--gold))]" />
              <span className="text-xs tracking-widest uppercase">
                {hero.eyebrow}
              </span>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Marquee = () => (
  <section aria-hidden className="py-8 border-y border-[hsl(var(--onyx)/0.08)]">
    <div className="overflow-hidden">
      <div className="marquee-track flex whitespace-nowrap gap-14 font-editorial text-3xl sm:text-4xl text-[hsl(var(--onyx)/0.7)]">
        {[...Array(2)].map((_, r) => (
          <div key={r} className="flex gap-14">
            {[
              "Nano Bonds",
              "Invisible Tapes",
              "Weaves",
              "Genius Wefts",
              "Keratin Bonds",
              "Maintenance",
              "Hair Loss Solutions",
              "Certified Educator",
            ].map((t) => (
              <span key={t + r} className="flex items-center gap-14">
                {t}
                <span className="text-[hsl(var(--gold))]">✦</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FeaturedServices = () => (
  <section data-testid="section-featured-services" className="py-24 sm:py-32">
    <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
      <div className="grid gap-6 lg:grid-cols-12 items-end mb-14">
        <div className="lg:col-span-8">
          <div className="overline">Featured Services</div>
          <h2 className="font-display text-4xl sm:text-6xl mt-4 tracking-tight text-balance">
            Every finish tailored, <span className="italic font-light">never rushed.</span>
          </h2>
        </div>
        <div className="lg:col-span-4 lg:text-right">
          <Link
            to="/services"
            data-testid="services-see-all"
            className="btn-pill btn-outline"
          >
            See all services <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>

      <Stagger className="grid gap-6 sm:gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {HAIR_SERVICES.map((s) => (
          <Item key={s.slug}>
            <Link
              to="/services"
              data-testid={`service-card-${s.slug}`}
              className="group block rounded-[2rem] overflow-hidden bg-white border border-[hsl(var(--onyx)/0.06)] transition-shadow duration-500 hover:shadow-[0_20px_60px_-20px_rgba(20,10,15,0.15)]"
            >
              <div className="aspect-[4/3] hover-zoom bg-blush">
                <img src={s.image} alt={s.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-7">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-2xl text-[hsl(var(--onyx))]">
                    {s.name}
                  </h3>
                  <span className="text-sm text-[hsl(var(--gold))] font-medium">
                    from {s.from}
                  </span>
                </div>
                <p className="mt-3 text-sm text-[hsl(var(--onyx)/0.6)] leading-relaxed">
                  {s.blurb}
                </p>
                <div className="mt-6 flex items-center justify-between text-xs text-[hsl(var(--onyx)/0.5)]">
                  <span className="uppercase tracking-widest">{s.duration}</span>
                  <span className="inline-flex items-center gap-1 text-[hsl(var(--onyx))] group-hover:text-[hsl(var(--gold))] transition-colors duration-300">
                    Explore <ArrowUpRight size={14} />
                  </span>
                </div>
              </div>
            </Link>
          </Item>
        ))}
      </Stagger>
    </div>
  </section>
);

const ExtensionsSection = () => (
  <section
    data-testid="section-extensions"
    className="relative py-24 sm:py-32 bg-blush-soft overflow-hidden"
  >
    <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
      <div className="grid gap-12 lg:grid-cols-12 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="overline">Hair Extension Specialists</div>
            <h2 className="font-display text-4xl sm:text-6xl mt-4 tracking-tight text-balance">
              Weightless length. <span className="italic font-light">Undetectable placement.</span>
            </h2>
            <p className="mt-6 text-[hsl(var(--onyx)/0.65)] leading-relaxed max-w-md">
              Nano Bonds, Custom K-Tips, Genius Wefts, Weaves and Keratin
              Bonds — precision-fitted for movement, longevity and salon-fresh
              shine.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/quiz"
                data-testid="extensions-quiz-cta"
                className="btn-pill btn-primary"
              >
                Find your method — 60s quiz <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/hair-extensions"
                data-testid="extensions-explore"
                className="btn-pill btn-outline"
              >
                Explore methods
              </Link>
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="extensions-consult"
                className="btn-pill btn-outline"
              >
                Free consultation
              </a>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Stagger className="grid grid-cols-2 gap-4 sm:gap-6">
            {EXTENSION_METHODS.slice(0, 4).map((m, i) => (
              <Item
                key={m.slug}
                className={i % 2 ? "sm:translate-y-10" : ""}
              >
                <div className="rounded-[1.75rem] overflow-hidden bg-white hover-zoom aspect-[4/5]">
                  <img src={m.image} alt={m.name} className="w-full h-full object-cover" />
                </div>
                <div className="mt-3 flex items-center justify-between px-1">
                  <span className="font-display text-lg text-[hsl(var(--onyx))]">
                    {m.name}
                  </span>
                  <span className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">
                    {m.tag}
                  </span>
                </div>
              </Item>
            ))}
          </Stagger>
        </div>
      </div>
    </div>
  </section>
);

const HairLossSection = () => (
  <section data-testid="section-hair-loss" className="py-24 sm:py-32">
    <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
      <div className="grid gap-12 lg:grid-cols-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="rounded-[2.25rem] overflow-hidden hover-zoom aspect-[5/4]">
            <img
              src={IMG.salonAlt}
              alt="Confidential consultation studio"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="overline">Hair Loss Solutions</div>
          <h2 className="font-display text-4xl sm:text-6xl mt-4 tracking-tight text-balance">
            Confidence, <span className="italic font-light">restored gently.</span>
          </h2>
          <p className="mt-6 text-[hsl(var(--onyx)/0.65)] leading-relaxed max-w-lg">
            Private, non-surgical solutions for thinning and hair loss — mesh
            integration, partial systems and premium toppers, colour-matched
            and completely natural.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {HAIR_LOSS.map((h) => (
              <div
                key={h.slug}
                className="rounded-2xl border border-[hsl(var(--onyx)/0.08)] p-5 bg-white"
              >
                <div className="font-display text-xl">{h.name}</div>
                <div className="mt-2 text-sm text-[hsl(var(--onyx)/0.6)] leading-relaxed">
                  {h.blurb}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link
              to="/hair-loss"
              data-testid="hair-loss-explore"
              className="btn-pill btn-primary"
            >
              Learn more <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const AcademyTeaser = () => (
  <section
    data-testid="section-academy"
    className="relative py-24 sm:py-32 bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] overflow-hidden"
  >
    <div className="mx-auto max-w-[1400px] px-6 sm:px-10 relative">
      <div className="grid gap-12 lg:grid-cols-12 items-center">
        <div className="lg:col-span-6">
          <div className="overline text-[hsl(var(--gold))]">NøoksPro Training Academy</div>
          <h2 className="font-display text-4xl sm:text-6xl mt-4 tracking-tight text-balance">
            Where the next generation of stylists is made.
          </h2>
          <p className="mt-6 text-[hsl(var(--cream)/0.7)] leading-relaxed max-w-lg">
            Certified courses in extensions, cutting, colour and hair-up —
            taught by an educator with 8+ years working the salon floor.
            Payment plans available.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/academy"
              data-testid="academy-enter"
              className="btn-pill btn-blush"
            >
              Explore courses <ArrowUpRight size={16} />
            </Link>
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="academy-enquire"
              className="btn-pill btn-outline border-white/20 text-[hsl(var(--cream))]"
            >
              Enquire
            </a>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              "Parent & Mini Hair Styling Workshop",
              "Future Stylist Programme",
              "Pin Curl Masterclass",
              "Hair Up Masterclass",
              "Accredited Blow Dry Course",
              "Single Method Hair Extensions Course",
              "Mixed Method Hair Extensions Course",
              "Ultimate Weave Course",
            ].map((c) => (
              <div
                key={c}
                className="rounded-full border border-white/15 px-4 py-2 text-xs text-[hsl(var(--cream)/0.8)] text-center"
              >
                {c}
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden hover-zoom">
            <img
              src={IMG.classroom}
              alt="NøoksPro Training Academy"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-6 left-6 right-6 glass-dark rounded-2xl p-5">
              <div className="overline text-[hsl(var(--gold))]">Accredited</div>
              <div className="font-display text-2xl mt-2 text-[hsl(var(--cream))]">
                Small group, one-to-one attention
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Reviews = () => (
  <section data-testid="section-reviews" className="py-24 sm:py-32">
    <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
        <div>
          <div className="overline">Client Reviews</div>
          <h2 className="font-display text-4xl sm:text-6xl mt-4 tracking-tight">
            Loved by 500+ women.
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              size={18}
              className="text-[hsl(var(--gold))]"
              fill="currentColor"
              strokeWidth={0}
            />
          ))}
          <span className="text-sm text-[hsl(var(--onyx)/0.6)] ml-1">
            5.0 · Google reviews
          </span>
        </div>
      </div>

      <Stagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {REVIEWS.map((r) => (
          <Item key={r.name}>
            <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-7 h-full flex flex-col">
              <div className="flex items-center gap-1 text-[hsl(var(--gold))]">
                {[...Array(r.stars)].map((_, i) => (
                  <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <p className="font-display text-xl mt-4 leading-snug text-[hsl(var(--onyx))]">
                “{r.text}”
              </p>
              <div className="mt-6 pt-6 border-t border-[hsl(var(--onyx)/0.06)] flex items-center justify-between text-sm">
                <span className="font-medium">{r.name}</span>
                <span className="text-[hsl(var(--onyx)/0.5)]">{r.source}</span>
              </div>
            </div>
          </Item>
        ))}
      </Stagger>
    </div>
  </section>
);

const InstagramFeed = ({ posts, handle }) => {
  const usingReal = Array.isArray(posts) && posts.length > 0;
  const displayHandle = handle ? `@${handle}` : SITE.instagramHandle;
  const followUrl = handle ? `https://www.instagram.com/${handle}` : SITE.instagramUrl;
  return (
    <section
      data-testid="section-instagram"
      className="py-24 sm:py-32 bg-blush-soft"
    >
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="overline">On Instagram</div>
            <h2 className="font-display text-4xl sm:text-6xl mt-4 tracking-tight">
              {displayHandle}
            </h2>
          </div>
          <a
            href={followUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="instagram-follow"
            className="btn-pill btn-primary"
          >
            <Instagram size={16} /> Follow
          </a>
        </div>

        <Stagger className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {usingReal ? (
            posts.map((p) => (
              <Item key={p.id}>
                <a
                  href={p.link || followUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`instagram-post-${p.id}`}
                  className="block aspect-square rounded-2xl overflow-hidden hover-zoom relative group"
                >
                  <img
                    src={p.image}
                    alt={(p.caption || "Instagram post").slice(0, 80)}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  {p.media_type === "VIDEO" && (
                    <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-black/60 text-white">
                      Video
                    </span>
                  )}
                </a>
              </Item>
            ))
          ) : (
            INSTA_TILES.map((src, i) => (
              <Item key={i}>
                <a
                  href={followUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block aspect-square rounded-2xl overflow-hidden hover-zoom"
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </a>
              </Item>
            ))
          )}
        </Stagger>
      </div>
    </section>
  );
};

const FAQSection = ({ faqs }) => (
  <section data-testid="section-faq" className="py-24 sm:py-32">
    <div className="mx-auto max-w-[1100px] px-6 sm:px-10">
      <div className="text-left mb-12">
        <div className="overline">Answers</div>
        <h2 className="font-display text-4xl sm:text-6xl mt-4 tracking-tight text-balance">
          Frequently asked.
        </h2>
      </div>
      <Accordion type="single" collapsible className="w-full">
        {(faqs || []).map((f, i) => (
          <AccordionItem
            key={i}
            value={`faq-${i}`}
            data-testid={`faq-item-${i}`}
            className="border-b border-[hsl(var(--onyx)/0.08)]"
          >
            <AccordionTrigger className="font-display text-xl sm:text-2xl text-left hover:no-underline py-6 text-[hsl(var(--onyx))]">
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-base text-[hsl(var(--onyx)/0.65)] leading-relaxed pb-8 max-w-2xl">
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

const ContactMap = () => (
  <section
    data-testid="section-contact-map"
    className="py-24 sm:py-32 bg-[hsl(var(--cream))]"
  >
    <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
      <div className="grid gap-10 lg:grid-cols-12 items-start">
        <div className="lg:col-span-5">
          <div className="overline">Visit / Contact</div>
          <h2 className="font-display text-4xl sm:text-6xl mt-4 tracking-tight text-balance">
            Come say hello.
          </h2>
          <p className="mt-6 text-[hsl(var(--onyx)/0.65)] leading-relaxed max-w-md">
            Step into the studio for a consultation, a fresh finish, or simply
            a coffee and a chat about your hair goals.
          </p>

          <div className="mt-8 space-y-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-blush flex items-center justify-center">
                <MapPin size={16} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">
                  Studio
                </div>
                <div className="font-display text-lg">{SITE.addressLine}</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-blush flex items-center justify-center">
                <Mail size={16} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">
                  Email
                </div>
                <a
                  href={`mailto:${SITE.email}`}
                  className="font-display text-lg"
                >
                  {SITE.email}
                </a>
              </div>
            </div>
          </div>

          <a
            href={SITE.bookingUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="contact-book-cta"
            className="btn-pill btn-primary mt-10"
          >
            Book Appointment <ArrowUpRight size={16} />
          </a>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-[2rem] overflow-hidden border border-[hsl(var(--onyx)/0.08)] aspect-[5/4]">
            <iframe
              title="Studio Map"
              src={SITE.mapEmbed}
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default function Home() {
  const [hero, setHero] = useState(DEFAULT_HERO);
  const [faqs, setFaqs] = useState(FALLBACK_FAQ);
  const [igPosts, setIgPosts] = useState([]);
  const [igHandle, setIgHandle] = useState(null);

  useEffect(() => {
    axios.get(`${API}/site/content`)
      .then((r) => {
        if (r.data?.hero) setHero({ ...DEFAULT_HERO, ...r.data.hero });
        if (Array.isArray(r.data?.faqs) && r.data.faqs.length) setFaqs(r.data.faqs);
      })
      .catch(() => {});
    axios.get(`${API}/instagram/feed`)
      .then((r) => {
        if (Array.isArray(r.data?.posts)) setIgPosts(r.data.posts);
        if (r.data?.handle) setIgHandle(r.data.handle);
      })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      <Hero hero={hero} />
      <Marquee />
      <FeaturedServices />
      <ExtensionsSection />
      <HairLossSection />
      <AcademyTeaser />
      <Reviews />
      <InstagramFeed posts={igPosts} handle={igHandle} />
      <FAQSection faqs={faqs} />
      <ContactMap />
    </div>
  );
}
